/**
 * URL building utilities for consistent URL construction across the application.
 * Provides fallback to window.location.origin if NEXT_PUBLIC_APPLICATION_URL is not set.
 */

const getApplicationUrl = (): string => {
  // Use environment variable if available, fallback to current origin
  if (process.env.NEXT_PUBLIC_APPLICATION_URL) {
    return process.env.NEXT_PUBLIC_APPLICATION_URL;
  }

  // Fallback to current domain (client-side only)
  if (globalThis.window !== undefined) {
    return globalThis.window.location.origin;
  }

  // If neither available (shouldn't happen in normal flows), warn and return empty string
  console.warn(
    'Unable to determine application URL. NEXT_PUBLIC_APPLICATION_URL not set and window.location unavailable.',
  );

  return '';
};

/**
 * Create an application URL with optional query parameters.
 * Handles proper URL encoding and gracefully falls back to current origin.
 *
 * @param pathname - The path (e.g., '/pm/scheduling/calendar')
 * @param params - Optional query parameters object
 * @returns Full URL string
 */
export const createAppUrl = (pathname: string, params?: Record<string, string | number | boolean>): string => {
  const baseUrl = getApplicationUrl();

  if (!baseUrl) {
    console.error('Cannot create URL: application base URL is empty');

    return pathname;
  }

  try {
    const url = new URL(pathname, baseUrl);

    if (params) {
      const searchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        searchParams.set(key, String(value));
      });
      url.search = searchParams.toString();
    }

    return url.toString();
  } catch (error) {
    console.error('Error creating URL:', error);

    return pathname;
  }
};

/**
 * Create a URL to the scheduling calendar with appointment details
 */
export const createSchedulingCalendarUrl = (appointmentId: string | number): string => {
  return createAppUrl('/pm/scheduling/calendar', {
    isEditing: appointmentId,
  });
};

/**
 * Create a URL to the scheduling calendar with work order details
 */
export const createWorkOrderCalendarUrl = (orderId: string | number): string => {
  return createAppUrl('/pm/scheduling/calendar', {
    isCreating: orderId,
  });
};
